import { FRAIS_CAT } from '../../constants/accounting';
import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { DEVISE, fmt } from '../../lib/currency';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

const chf = (v) => DEVISE + ' ' + fmt(v);
const cellule = { padding: '8px 6px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' };

/**
 * « Mon année » sur l'accueil : l'essentiel dans un cadre, puis ce que
 * l'entreprise doit à l'État — TVA, AVS, impôts —, ce qui est déjà payé et
 * ce qui reste.
 *
 * <p>Les quatre étapes du calcul répétaient le cadre et allongeaient la page ;
 * le détail des dépenses, type par type, est dans le classeur, et le calcul
 * complet dans la Compta.
 *
 * @param {object} props propriétés
 * @param {string} props.periode la période lue (« 2026 », « octobre 2026 »…)
 * @param {number} props.factureTtc facturé TVA comprise, comme la comptabilité le range
 * @param {number} props.ca facturé hors TVA
 * @param {number} props.depenses dépenses hors TVA récupérable
 * @param {number} props.depensesTtc ce qui est sorti du compte pour ces dépenses,
 *   plus les honoraires du comptable pas encore payés
 * @param {number} [props.comptableAPayer] honoraires du comptable de la période pas encore payés
 * @param {number} props.tvaAPayer TVA facturée moins TVA récupérable
 * @param {{estimees: number}} [props.tvaServeur] position TVA du serveur
 * @param {number} props.benefice bénéfice hors TVA
 * @param {number} props.tresorerie argent encaissé moins argent dépensé
 * @param {{total: number, avs?: number, revenu?: number, societe?: number, capital?: number}} props.impots
 *   impôts à mettre de côté (impotsAProvisionner)
 * @param {{tva: number, avs: number, impots: number}} [props.dejaPaye] versé à l'État
 *   sur la période (Dépenses → Payé à l'État)
 * @param {boolean} props.independant raison individuelle : AVS et impôt sur le revenu
 * @param {object} props.TX taux de la comptabilité
 * @param {(module: string, params?: object) => void} props.onNav navigation
 * @returns {import('react').ReactElement} le cadre et la carte de l'État
 */
export function MonAnnee({
  periode,
  factureTtc,
  ca,
  depenses,
  depensesTtc,
  comptableAPayer = 0,
  tvaAPayer,
  tvaServeur: _tvaServeur,
  benefice,
  tresorerie,
  impots,
  dejaPaye = { tva: 0, avs: 0, impots: 0 },
  independant,
  TX,
  onNav,
}) {
  const reste = benefice - impots.total;
  // Ce que l'État attend, ce qui est déjà versé, et le reste — une ligne par
  // impôt. Une Sàrl ou une SA paie l'impôt sur le bénéfice et sur le capital.
  const lignes = [
    // Un crédit de TVA (plus de TVA payée sur les achats qu'encaissée) reste
    // négatif : l'AFC le rembourse, et il réduit le total. Il s'affichait
    // 0.00, et le total l'ignorait.
    [tvaAPayer >= 0 ? tr('TVA') : tr('TVA à récupérer'), tvaAPayer, dejaPaye.tva],
    ...(independant
      ? [
          [
            // Le taux réellement appliqué : le barème dégressif le baisse
            // sous 60'500.– de revenu ; sous 10'100.–, la cotisation minimale.
            impots.avsMinimum
              ? tr('AVS / AI / APG') + ' (' + trf('Minimum {0}', fmt(impots.avs)) + ')'
              : trf(
                  'AVS / AI / APG ({0} %)',
                  impots.base > 0
                    ? Math.round((impots.avs / impots.base) * 1000) / 10
                    : TX.avsIndependant,
                ),
            impots.avs,
            dejaPaye.avs,
          ],
          [trf('Impôts sur le revenu (≈ {0} %)', TX.impotRevenu), impots.revenu, dejaPaye.impots],
        ]
      : [[tr('Impôts (bénéfice et capital)'), impots.total, dejaPaye.impots]]),
  ];
  const totalDu = lignes.reduce((t, l) => t + l[1], 0);
  const totalPaye = lignes.reduce((t, l) => t + l[2], 0);

  const rangee = (lb, v, fort) => (
    <div
      key={lb}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 10,
        padding: '6px 0',
        borderBottom: '1px solid rgba(255,255,255,.1)',
      }}
    >
      <span style={{ fontSize: 10.5, opacity: 0.85, paddingLeft: lb.startsWith(' ') ? 20 : 0 }}>
        {lb.trim()}
      </span>
      <b style={{ fontSize: fort ? 14 : 12.5, whiteSpace: 'nowrap' }}>{chf(v)}</b>
    </div>
  );
  const resteDe = (du, paye) => {
    const r = Math.round((du - paye) * 100) / 100;
    if (r < 0 && du < 0) {
      return <span style={{ color: C.green }}>{trf('à recevoir {0}', fmt(-r))}</span>;
    }
    // Payé plus que dû : le dire, plutôt qu'un 0.00 qui cache l'écart.
    if (r < 0) return <span style={{ color: C.amber }}>{trf('trop payé {0}', fmt(-r))}</span>;
    return <span style={{ color: r > 0 ? C.red : C.green }}>{fmt(r)}</span>;
  };

  return (
    <>
      <div
        data-testid="mon-annee"
        onClick={() => onNav('compta')}
        style={{
          background: C.navy,
          color: '#fff',
          borderRadius: 15,
          padding: '13px 14px',
          marginBottom: 12,
          cursor: 'pointer',
        }}
      >
        <div
          style={{
            fontSize: 9,
            fontWeight: 800,
            opacity: 0.65,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            marginBottom: 2,
          }}
        >
          {trf('Mon année {0} en un coup d’œil', periode)}
        </div>
        {rangee('🧾 ' + tr('Facturé avec TVA'), factureTtc, true)}
        {rangee(' ' + tr('sans TVA'), ca)}
        {rangee('📦 ' + tr('Dépenses avec TVA'), depensesTtc)}
        {rangee(' ' + tr('sans TVA'), depenses)}
        {comptableAPayer > 0.005 &&
          rangee(' ' + tr('dont comptable, pas encore payé'), comptableAPayer)}
        {rangee('📈 ' + tr('Bénéfice'), benefice)}
        {rangee('🏦 ' + tr('Sur le compte (trésorerie)'), tresorerie)}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 10,
            background: reste >= 0 ? C.green : C.red,
            borderRadius: 11,
            padding: '9px 11px',
            marginTop: 10,
          }}
        >
          <span style={{ fontSize: 10.5, fontWeight: 800 }}>
            {independant ? tr('✅ Pour moi, après AVS et impôts') : tr('✅ Bénéfice après impôts')}
          </span>
          <b style={{ fontSize: 18, whiteSpace: 'nowrap' }}>{chf(reste)}</b>
        </div>
      </div>

      {/* Ce que je dois à l'État : à payer, déjà payé (les paiements scannés
          dans Dépenses → Payé à l'État), reste. */}
      <Card style={{ marginBottom: 12 }}>
        <div data-testid="du-a-l-etat">
          <Bar
            icon="🏛"
            title={tr('Ce que je dois à l’État')}
            color={C.purple}
            sub={periode + ' · ' + tr('à payer, déjà payé, reste')}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10.5 }}>
            <thead>
              <tr style={{ color: C.muted, fontSize: 8.5 }}>
                <th />
                <th style={{ ...cellule, fontWeight: 800 }}>{tr('À payer')}</th>
                <th style={{ ...cellule, fontWeight: 800 }}>{tr('Déjà payé')}</th>
                <th style={{ ...cellule, fontWeight: 800, paddingRight: 13 }}>{tr('Reste')}</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map(([lb, du, paye]) => (
                <tr key={lb} style={{ borderTop: '1px solid ' + C.border, color: C.ink }}>
                  <td style={{ padding: '8px 6px 8px 13px', fontWeight: 600 }}>{lb}</td>
                  <td style={{ ...cellule, ...(du < 0 ? { color: C.green } : {}) }}>{fmt(du)}</td>
                  <td style={cellule}>{fmt(paye)}</td>
                  <td style={{ ...cellule, paddingRight: 13 }}>{resteDe(du, paye)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: '1px solid ' + C.border, background: C.navyXL }}>
                <td style={{ padding: '9px 6px 9px 13px', fontWeight: 900 }}>{tr('Total')}</td>
                <td style={{ ...cellule, fontWeight: 900 }}>{fmt(totalDu)}</td>
                <td style={{ ...cellule, fontWeight: 900 }}>{fmt(totalPaye)}</td>
                <td style={{ ...cellule, fontWeight: 900, paddingRight: 13 }}>
                  {resteDe(totalDu, totalPaye)}
                </td>
              </tr>
            </tbody>
          </table>
          {/* La TVA calculée sur le montant (relevé, ticket sans TVA écrite)
              est le travail normal de SwissTech : « 28 achats sans TVA… »
              n'apprenait rien au patron, le message est retiré. */}
          {(dejaPaye.doublons || []).map((d) => (
            <div
              key={d.type + d.montant}
              role="alert"
              style={{
                padding: '6px 13px',
                fontSize: 9.5,
                fontWeight: 700,
                color: C.red,
                background: C.red + '10',
                borderTop: '1px solid ' + C.border,
                lineHeight: 1.45,
              }}
            >
              {trf(
                '⚠️ {0} : {1} paiements de {2} — un doublon ? Vérifie dans Dépenses.',
                tr((FRAIS_CAT[d.type] || {}).lib || d.type),
                d.n,
                DEVISE + ' ' + fmt(d.montant),
              )}
            </div>
          ))}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 10,
              padding: '8px 13px 10px',
              borderTop: '1px solid ' + C.border,
            }}
          >
            <span style={{ fontSize: 8.5, color: C.muted, lineHeight: 1.45 }}>
              {tr(
                '« Déjà payé » : tes paiements scannés dans Dépenses → Payé à l’État. AVS et impôts : estimation, taux dans Compta.',
              )}
            </span>
            <button
              type="button"
              onClick={() => onNav('depenses', { nouvelle: true })}
              style={{
                flexShrink: 0,
                border: '1px solid ' + C.purple,
                background: '#fff',
                color: C.purple,
                borderRadius: 9,
                padding: '6px 9px',
                fontSize: 9.5,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              ＋ {tr('Paiement')}
            </button>
          </div>
        </div>
      </Card>
    </>
  );
}
